import React, { useEffect, useState } from "react";
import { Container, Row, Col, Spinner, Button, Alert, Card } from "react-bootstrap";
import { useNavigate, Navigate } from "react-router-dom";
import { API, graphqlOperation } from "aws-amplify";
import { useAuth } from "../contexts/AuthContext.tsx";
import { listSubmissions } from "../graphql/queries.ts";
import Header from "./Header.tsx";
import { palette, fonts } from "./style.tsx";

interface Submission {
  submissionId: string;
  company_id: string;
  complaints: string;
  dateOccurred?: string | null;
  generatedEmail?: string | null;
  timestamp?: number | null;
  userId?: string | null;
}

const cardStyle: React.CSSProperties = {
  background: "rgba(255, 255, 255, 0.08)",
  border: "1px solid rgba(255, 255, 255, 0.15)",
  borderRadius: 12,
  padding: "16px 20px",
  color: palette.textColor,
  fontFamily: fonts.baseFontFamily,
};

const MySnitchesScreen: React.FC = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        // NOTE: filter-based lookup — acceptable at current volume. If submissions
        // grow past ~1k, migrate to a GSI-backed query (listSubmissionsByUser).
        const result: any = await API.graphql(
          graphqlOperation(listSubmissions, {
            filter: { userId: { eq: user.sub } },
            limit: 100,
          })
        );
        const items: Submission[] = result?.data?.listSubmissions?.items ?? [];
        items.sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0));
        setSubmissions(items);
      } catch (err: any) {
        console.error("Failed to load submissions:", err);
        setError("Couldn't load your snitches. Try again.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  if (authLoading) {
    return (
      <Container fluid className="mt-4 px-3 text-center">
        <Spinner animation="border" style={{ color: palette.textColor }} />
      </Container>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Container fluid className="mt-4 px-3" style={{ paddingBottom: 40 }}>
      <Header onReset={() => navigate("/")} />
      <Row className="justify-content-center mt-3">
        <Col xs={12} md={10} lg={8}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3 style={{ color: palette.textColor, fontFamily: fonts.baseFontFamily, margin: 0 }}>My Snitches</h3>
              <Button
                onClick={() => navigate("/app")}
                style={{ backgroundColor: palette.secondaryColor, border: "none", borderRadius: 50 }}
              >
                + File New
              </Button>
            </div>

            {loading && (
              <div className="text-center py-5">
                <Spinner animation="border" style={{ color: palette.textColor }} />
              </div>
            )}

            {error && <Alert variant="danger">{error}</Alert>}

            {!loading && !error && submissions.length === 0 && (
              <Card style={cardStyle} className="text-center">
                <Card.Body>
                  <p style={{ marginBottom: 16 }}>You haven't filed any complaints yet.</p>
                  <Button
                    onClick={() => navigate("/app")}
                    style={{ backgroundColor: palette.secondaryColor, border: "none", borderRadius: 50 }}
                  >
                    File Your First Complaint
                  </Button>
                </Card.Body>
              </Card>
            )}

            {!loading && submissions.map((s) => {
              const filed = s.timestamp ? new Date(s.timestamp * 1000).toLocaleDateString() : "Unknown";
              const preview = (s.generatedEmail || "").slice(0, 160);
              return (
                <Card key={s.submissionId} style={{ ...cardStyle, marginBottom: 12 }}>
                  <Card.Body>
                    <div className="d-flex justify-content-between flex-wrap">
                      <div>
                        <div style={{ fontSize: "1.05rem", fontWeight: 700 }}>{s.company_id}</div>
                        <div style={{ fontSize: "0.82rem", color: palette.accentColor }}>
                          Snitch ID: {s.submissionId.slice(0, 8)} · Filed {filed}
                        </div>
                      </div>
                    </div>
                    {preview && (
                      <div style={{ marginTop: 10, fontSize: "0.9rem", opacity: 0.85 }}>
                        {preview}{s.generatedEmail && s.generatedEmail.length > 160 ? "…" : ""}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              );
            })}
        </Col>
      </Row>
    </Container>
  );
};

export default MySnitchesScreen;
